import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TimelineControls } from './TimelineControls';
import { TimelineDaysCalendar } from './TimelineDaysCalendar';
import { TimelineFloatingAdd } from './TimelineFloatingAdd';
import { TimelinePeriod } from './TimelinePeriod';
import { TimelineViewProps, TimelineViewProvider } from './TimelineViewContext';
import { TimelineYearCalendar } from './TimelineYearCalendar';
import { TimelineYearsCalendar } from './TimelineYearsCalendar';
import { getPeriodDate } from './timelineViewUtils';

export const TimelineView: React.FC<TimelineViewProps> = (props) => {
    const { draftSelection, onCancelDraftSelection } = props;
    const [daysLabel, setDaysLabel] = useState('');
    const [daysDate, setDaysDate] = useState<Date | null>(null);
    const [creationMenuKey, setCreationMenuKey] = useState<string | null>(null);
    const creationMenuRef = useRef<HTMLDivElement>(null);
    const handleActiveMonthChange = useCallback((label: string, date: Date) => {
        setDaysLabel(label);
        setDaysDate(date);
    }, []);
    useEffect(() => {
        if (!creationMenuKey) return undefined;
        const close = (event: PointerEvent) => {
            if (!creationMenuRef.current?.contains(event.target as Node)) setCreationMenuKey(null);
        };
        document.addEventListener('pointerdown', close);
        return () => document.removeEventListener('pointerdown', close);
    }, [creationMenuKey]);
    useEffect(() => {
        if (!draftSelection) return undefined;
        const closeDraft = (event: PointerEvent) => {
            const target = event.target;
            if (target instanceof Element && target.closest('.draft-selection-preview')) return;
            onCancelDraftSelection();
            if (target instanceof Element && target.closest('.timeline-rows')) {
                event.preventDefault();
                event.stopPropagation();
            }
        };
        document.addEventListener('pointerdown', closeDraft, true);
        return () => document.removeEventListener('pointerdown', closeDraft, true);
    }, [draftSelection, onCancelDraftSelection]);
    const periods = Array.from(
        { length: props.timelineWindow.end - props.timelineWindow.start + 1 },
        (_, index) => {
            const offset = props.timelineWindow.start + index;
            return { date: getPeriodDate(props.selectedDate, props.timelineScale, offset), offset };
        }
    );
    return (
        <TimelineViewProvider {...props}
            activeTimelineLabel={props.timelineScale === 'month' ? daysLabel : props.activeTimelineLabel}
            creationMenuKey={creationMenuKey}
            creationMenuRef={creationMenuRef} setCreationMenuKey={setCreationMenuKey}
            timelineZoomAnchorDate={props.timelineScale === 'month' ? daysDate : null}>
            <div className="timeline-view">
                <TimelineControls />
                {props.timelineScale === 'month' ? (
                    <TimelineDaysCalendar scrollContainerRef={props.scrollContainerRef}
                        onActivePeriodChange={handleActiveMonthChange} />
                ) : props.timelineScale === 'year' ? (
                    <>{periods.map(({ date, offset }) => (
                        <TimelineYearCalendar key={`year-${date.getFullYear()}`} date={date} current={offset === 0} />
                    ))}</>
                ) : props.timelineScale === 'years' ? (
                    <>{periods.map(({ date, offset }) => (
                        <TimelineYearsCalendar key={`years-${date.getFullYear()}`} date={date} current={offset === 0} />
                    ))}</>
                ) : periods.map(({ date, offset }) => (
                    <TimelinePeriod key={`${props.timelineScale}-${date.toISOString()}`}
                        periodDate={date} offset={offset} />
                ))}
                <TimelineFloatingAdd />
            </div>
        </TimelineViewProvider>
    );
};
